import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShoppingCart, ChevronLeft, ChevronRight, ArrowDownSquare, ArrowBigRight, ArrowRightCircle } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import '../pop/pop.css'

import { fetchCombos } from "../../api/combos.api";
import { getNewestItemRank } from "../../utils/sortDisplayOrder";



const ComboListing = () => {
    const [combos, setCombos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);

    const navigate = useNavigate();
    const ITEMS_PER_PAGE = 8;


    const [selectedBrand, setSelectedBrand] = useState("ALL");
    const [selectedDiscounts, setSelectedDiscounts] = useState(new Set());
    const [sortBy, setSortBy] = useState("recommended");
    const [activePage, setActivePage] = useState(1);


    const {
        updateCart,
        getComboQuantity,
        isComboInCart,
    } = useCart();

    const { isAuthenticated, setShowLoginModal, isMember } = useAuth();




    useEffect(() => {
        let mounted = true;

        fetchCombos()
            .then((data) => {
                if (!mounted) return;
                setCombos(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!mounted) return;
                setCombos([]);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, []);


    const handleAddToCart = (product) => {
        if (!isAuthenticated) {
            setShowLoginModal(true);
            return;
        }
        if (product.max_unit && product.max_unit <= 0) return;
        updateCart({
            type: "combo",
            id: product.id,
            delta: 1,
        })
    }

    const getBrandLabel = (combo) => {
        const rawBrand = String(combo?.brand ?? "").trim();
        const normalized = rawBrand.toLowerCase();

        if (
            !rawBrand ||
            ["none", "null", "undefined", "na", "n/a", "-", "—"].includes(
                normalized
            )
        ) {
            return "UNBRANDED";
        }

        return rawBrand;
    };

    const brands = [
        { name: "ALL", count: combos.length },
        ...Object.values(
            combos.reduce((acc, combo) => {
                const brandName = getBrandLabel(combo);

                if (!acc[brandName]) {
                    acc[brandName] = {
                        name: brandName,
                        count: 0,
                    };
                }
                acc[brandName].count += 1;
                return acc;
            }, {})
        ),
    ];


    const getDiscountPercentage = (mrp, price) => {
        if (!mrp || mrp <= price) return 0;
        return ((mrp - price) / mrp) * 100;
    };

    const matchesDiscountFilter = (combo) => {
        if (selectedDiscounts.size === 0) return true;

        const discount = getDiscountPercentage(
            combo.combo_price,
            combo.price
        );

        for (let range of selectedDiscounts) {
            if (range === "Upto 10%" && discount <= 10) return true;
            if (range === "10% - 20%" && discount > 10 && discount <= 20) return true;
            if (range === "20% - 30%" && discount > 20 && discount <= 30) return true;
            if (range === "30% - 40%" && discount > 30 && discount <= 40) return true;
            if (range === "40%-50%" && discount > 40 && discount <= 50) return true;
        }
        return false;
    };


    const filteredCombos = combos.filter((combo) => {
        if (
            selectedBrand !== "ALL" &&
            getBrandLabel(combo) !== selectedBrand
        )
            return false;

        // Price filter is temporarily disabled.

        if (!matchesDiscountFilter(combo)) return false;

        return true;
    });


    const sortedCombos = [...filteredCombos].sort((a, b) => {
        if (sortBy === "recommended") {
            return getNewestItemRank(b) - getNewestItemRank(a);
        }
        if (sortBy === "price-low") return a.price - b.price;
        if (sortBy === "price-high") return b.price - a.price;
        if (sortBy === "date-new") {
            return getNewestItemRank(b) - getNewestItemRank(a);
        }
        if (sortBy === "date-old") {
            return getNewestItemRank(a) - getNewestItemRank(b);
        }
        return 0;
    });


    const totalPages = Math.ceil(
        sortedCombos.length / ITEMS_PER_PAGE
    );
    const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
    const currentProducts = sortedCombos.slice(
        startIndex,
        startIndex + ITEMS_PER_PAGE
    );


    const handleDiscountChange = (range) => {
        setSelectedDiscounts((prev) => {
            const next = new Set(prev);
            next.has(range) ? next.delete(range) : next.add(range);
            return next;
        });
        setActivePage(1);
    };




    if (loading) {
        return (
            <div className="loading-wrap">
                <div className="loader" />
                <p>Loading combos…</p>
            </div>
        );
    }


    return (
        <div className="page">
            <Helmet>
                <title>Combo Sets & Bundle Deals | 164 Diecast Zone</title>
                <meta
                    name="description"
                    content="Shop diecast combo sets and bundle deals at 164 Diecast Zone — save more on curated 1:64 scale model car collections."
                />
                <link rel="canonical" href="https://164diecastzone.com/combos" />
                <meta property="og:title" content="Combo Sets & Bundle Deals | 164 Diecast Zone" />
                <meta
                    property="og:description"
                    content="Shop diecast combo sets and bundle deals at 164 Diecast Zone — save more on curated 1:64 scale model car collections."
                />
                <meta property="og:url" content="https://164diecastzone.com/combos" />
            </Helmet>

            {/* BANNER */}
            <div className="banner">
                <div className="banner-container">
                    <div className="banner-box">
                        {/* <div className="banner-text">
                            <img src={POP} alt={selectedBrand} />
                            <h1 className="banner-brand-title">{selectedBrand.toUpperCase()}</h1>
                        </div> */}
                    </div>
                </div>
            </div>

            {/* BREADCRUMB */}
            <div className="breadcrumb11">
                <div className="page-container">
                    <span onClick={() => navigate("/")}>Home</span>
                    <span className="highlight"> / </span>{" "}
                    <span>{selectedBrand}</span>
                    <span className="highlight"> / </span>{" "}
                    {selectedBrand.length}
                </div>

            </div>


            <div className="main-content">
                <div className="content-wrapper">


                    <div className="sidebar">


                        {/* <div className="filter-box">
                            <h3 className="filter-title">SET TYPE</h3>

                            <div className="filter-options">
                                {setTypeCounts.map((set) => (
                                    <div
                                        key={set.name}
                                        className={`brand-nav-item ${selectedSet === set.name ? "brand-nav-active" : ""
                                            }`}
                                        onClick={() => {
                                            setSelectedSet(set.name);
                                            setActivePage(1);
                                        }}
                                    >
                                        <span>{set.name}</span>
                                        <span className="filter-count">{set.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div> */}




                        <div className="filter-box">
                            <h3 className="filter-title">BRANDS</h3>
                            <div className="filter-options">
                                {brands.map((brand) => (
                                    <div
                                        key={brand.name}
                                        className={`brand-nav-item ${selectedBrand === brand.name ? "brand-nav-active" : ""
                                            }`}
                                        onClick={() => {
                                            setSelectedBrand(brand.name);
                                            setActivePage(1);
                                        }}
                                    >
                                        <span>{brand.name}</span>
                                        <span className="filter-count">{brand.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* <div className="filter-box">
                            <h3 className="filter-title">PRICES</h3>

                            <div className="price-range">
                                <span className="price-label">Range:</span>
                                ₹{priceRange[0]} - ₹{priceRange[1]}
                            </div>

                            <div className="dual-slider">
                                <input
                                    type="range"
                                    min="0"
                                    max={sliderMaxPrice}
                                    step="100"
                                    value={priceRange[0]}
                                    onChange={(e) => {
                                        const value = Math.min(Number(e.target.value), priceRange[1] - 100);
                                        setPriceRange([value, priceRange[1]]);
                                        setActivePage(1);
                                    }}
                                    className="thumb thumb-left"
                                />

                                <input
                                    type="range"
                                    min="0"
                                    max={sliderMaxPrice}
                                    step="100"
                                    value={priceRange[1]}
                                    onChange={(e) => {
                                        const value = Math.max(Number(e.target.value), priceRange[0] + 100);
                                        setPriceRange([priceRange[0], value]);
                                        setActivePage(1);
                                    }}
                                    className="thumb thumb-right"
                                />

                                <div className="slider-track" />
                                <div
                                    className="slider-range"
                                    style={{
                                        left: `${(priceRange[0] / sliderMaxPrice) * 100}%`,
                                        right: `${100 - (priceRange[1] / sliderMaxPrice) * 100}%`,
                                    }}
                                />
                            </div>
                        </div> */}

                        {/* <div className="filter-box">
                            <h3 className="filter-title">DISCOUNT</h3>
                            <div className="filter-options">
                                {["Upto 10%", "10% - 20%", "20% - 30%", "30% - 40%", "40%-50%"].map(range => (
                                    <label key={range} className="filter-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedDiscounts.has(range)}
                                            onChange={() => handleDiscountChange(range)}
                                        />
                                        {range}
                                    </label>
                                ))}
                            </div>
                        </div> */}

                    </div>

                    {/* LIST */}
                    <div className="products-section">
                        <div className="mobile-filter-bar">
                            <span className="mobile-filter-label">Refine</span>

                            <button
                                className="mobile-filter-btn"
                                onClick={() => setFilterOpen(true)}
                            >
                                Filters
                            </button>
                        </div>


                        <div className="sort-bar">
                            <div className="sort-label">Sort By:</div>
                            <select
                                value={sortBy}
                                onChange={(e) => {
                                    setSortBy(e.target.value);
                                    setActivePage(1);
                                }}
                                className="sort-select"
                            >
                                <option value="recommended">Recommended</option>
                                <option value="price-high">Price, High To Low</option>
                                <option value="price-low">Price, Low To High</option>
                                <option value="date-new">Date, New to Old</option>
                                <option value="date-old">Date, Old to New</option>
                            </select>
                        </div>

                        {/* GRID */}
                        <div className="products-grid">
                            {currentProducts.length > 0 ? (
                                currentProducts.map((product) => {

                                    const isCombo = true;
                                    const basePoints = Number(product.points);
                                    const memberPoints = Number(
                                        product.member_points ?? product.memberPoints
                                    );
                                    const displayPoints =
                                        isMember ? memberPoints : basePoints;
                                    const showPointsBadge =
                                        Number.isFinite(displayPoints) &&
                                        displayPoints > 0;


                                    const comboQty = isCombo
                                        ? getComboQuantity(product.id)
                                        : 0;


                                    return (
                                        <div key={product.id} className="product-card">


                                            <div className="product-image-wrapper">

                                                {showPointsBadge && (
                                                    <div className="product-points-badge">
                                                        ₹{displayPoints} Rewards
                                                    </div>
                                                )}

                                                {isCombo && product.products.length > 1 && (
                                                    <div className="product-badge">
                                                        {product.products.length}-SET COMBO
                                                    </div>
                                                )}


                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="product-image"
                                                    onClick={() => navigate(`/combo/${product.id}`)}
                                                />
                                            </div>


                                            <div className="product-info">
                                                <h4
                                                    className="product-name"
                                                    onClick={() => navigate(`/combo/${product.id}`)}
                                                >
                                                    {product.name}
                                                </h4>
                                                <div className="product-brand">
                                                    {getBrandLabel(product)}
                                                </div>


                                                {isMember && product.combo_price > product.price ? (
                                                    <>
                                                        <div className="product-mrp">
                                                            MRP: <span className="strikethrough">₹{product.combo_price}</span>
                                                        </div>

                                                        <div className="product-pricing">
                                                            <span className="product-price">₹{product.price}</span>

                                                            {Math.round(
                                                                getDiscountPercentage(product.combo_price, product.price)
                                                            ) >= 10 && (
                                                                    <span className="product-discount">
                                                                        {Math.round(
                                                                            getDiscountPercentage(product.combo_price, product.price)
                                                                        )}% OFF
                                                                    </span>
                                                                )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="product-pricing">
                                                        <span className="product-price">₹{product.price}</span>
                                                    </div>
                                                )}


                                                {/* ACTIONS */}
                                                <div className="product-actions">


                                                    {isCombo && (
                                                        isComboInCart(product.id) ? (
                                                            <div className="quantity-selector">
                                                                <button
                                                                    className="quantity-btn"
                                                                    onClick={() =>
                                                                        updateCart({
                                                                            type: "combo",
                                                                            id: product.id,
                                                                            delta: -1,
                                                                        })
                                                                    }
                                                                >
                                                                    -
                                                                </button>

                                                                <span className="quantity-value">
                                                                    {getComboQuantity(product.id)}
                                                                </span>

                                                                <button
                                                                    className="quantity-btn"
                                                                    onClick={() =>
                                                                        updateCart({
                                                                            type: "combo",
                                                                            id: product.id,
                                                                            delta: 1,
                                                                        })
                                                                    }
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                className="add-to-cart-btn"
                                                                onClick={() =>
                                                                    handleAddToCart(product)
                                                                }
                                                            >
                                                                Add To Cart
                                                            </button>
                                                        )
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="no-products">
                                    <p>No items found matching your filters.</p>
                                </div>
                            )}
                        </div>


                        {/* PAGINATION */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    className="pagination-btn"
                                    disabled={activePage === 1}
                                    onClick={() => setActivePage(p => p - 1)}
                                >
                                    <ChevronLeft size={18} />
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        className={`pagination-btn ${activePage === p ? "pagination-active" : ""}`}
                                        onClick={() => setActivePage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}

                                <button
                                    className="pagination-btn"
                                    disabled={activePage === totalPages}
                                    onClick={() => setActivePage(p => p + 1)}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            </div>
            <div className={`mobile-filter-drawer ${filterOpen ? "open" : ""}`}>
                <div className="drawer-header">
                    <span>Filters</span>
                    <button onClick={() => setFilterOpen(false)}>✕</button>
                </div>
                <div className="drawer-body">
                    <div className="sidebar">

                        {/* SET FILTER */}
                        {/* <div className="filter-box">
                            <h3 className="filter-title">SET TYPE</h3>

                            <div className="filter-options">
                                {setTypeCounts.map((set) => (
                                    <div
                                        key={set.name}
                                        className={`brand-nav-item ${selectedSet === set.name ? "brand-nav-active" : ""
                                            }`}
                                        onClick={() => {
                                            setSelectedSet(set.name);
                                            setActivePage(1);
                                        }}
                                    >
                                        <span>{set.name}</span>
                                        <span className="filter-count">{set.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div> */}



                        {/* BRAND FILTER */}
                        <div className="filter-box">
                            <h3 className="filter-title">BRANDS</h3>
                            <div className="filter-options">
                                {brands.map((brand) => (
                                    <div
                                        key={brand.name}
                                        className={`brand-nav-item ${selectedBrand === brand.name ? "brand-nav-active" : ""
                                            }`}
                                        onClick={() => {
                                            setSelectedBrand(brand.name);
                                            setActivePage(1);
                                        }}
                                    >
                                        <span>{brand.name}</span>
                                        <span className="filter-count">{brand.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* <div className="filter-box">
                            <h3 className="filter-title">PRICES</h3>

                            <div className="price-range">
                                <span className="price-label">Range:</span>
                                ₹{priceRange[0]} - ₹{priceRange[1]}
                            </div>

                            <div className="dual-slider">
                                <input
                                    type="range"
                                    min="0"
                                    max={sliderMaxPrice}
                                    step="100"
                                    value={priceRange[0]}
                                    onChange={(e) => {
                                        const value = Math.min(Number(e.target.value), priceRange[1] - 100);
                                        setPriceRange([value, priceRange[1]]);
                                        setActivePage(1);
                                    }}
                                    className="thumb thumb-left"
                                />

                                <input
                                    type="range"
                                    min="0"
                                    max={sliderMaxPrice}
                                    step="100"
                                    value={priceRange[1]}
                                    onChange={(e) => {
                                        const value = Math.max(Number(e.target.value), priceRange[0] + 100);
                                        setPriceRange([priceRange[0], value]);
                                        setActivePage(1);
                                    }}
                                    className="thumb thumb-right"
                                />

                                <div className="slider-track" />
                                <div
                                    className="slider-range"
                                    style={{
                                        left: `${(priceRange[0] / sliderMaxPrice) * 100}%`,
                                        right: `${100 - (priceRange[1] / sliderMaxPrice) * 100}%`,
                                    }}
                                />
                            </div>
                        </div> */}

                        {/* DISCOUNT FILTER */}
                        <div className="filter-box">
                            <h3 className="filter-title">DISCOUNT</h3>
                            <div className="filter-options">
                                {["Upto 10%", "10% - 20%", "20% - 30%", "30% - 40%", "40%-50%"].map(range => (
                                    <label key={range} className="filter-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedDiscounts.has(range)}
                                            onChange={() => handleDiscountChange(range)}
                                        />
                                        {range}
                                    </label>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <div
                className={`drawer-backdrop ${filterOpen ? "show" : ""}`}
                onClick={() => setFilterOpen(false)}
            />

        </div>
    );
};

export default ComboListing;
